import { Headphones, HeadphoneOff, Mic, MicOff, PhoneOff, ScreenShare, ScreenShareOff, Settings, Signal, Smile, Video, VideoOff, X } from '@/components/ui/icons';
import { useRef, useState } from 'react';
import { Link } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import type { UserStatusPreference } from '@clanfire/shared';
import { errorMessage } from '@/api/client';
import { userApi } from '@/api/endpoints';
import { StyledName } from '@/components/profile/StyledName';
import { Avatar } from '@/components/ui/Avatar';
import { IconButton } from '@/components/ui/Button';
import { SoundboardButton } from '@/components/voice/Soundboard';
import { Menu, type MenuEntry } from '@/components/ui/Popover';
import { describeExpiry } from '@/lib/customStatus';
import { screenShareSupported, voiceManager } from '@/realtime/voice';
import { useAuth } from '@/stores/auth';
import { useVoice } from '@/stores/realtime';
import { useServers } from '@/stores/servers';
import { toast, useUi } from '@/stores/ui';
import { usePresenceOf } from '@/stores/users';

const STATUS_OPTIONS: Array<{ value: UserStatusPreference; label: string; dot: string; hint?: string }> = [
  { value: 'ONLINE', label: 'Online', dot: 'bg-online' },
  { value: 'IDLE', label: 'Idle', dot: 'bg-idle' },
  { value: 'DND', label: 'Do Not Disturb', dot: 'bg-dnd', hint: 'Mutes notifications' },
  { value: 'INVISIBLE', label: 'Invisible', dot: 'bg-offline', hint: 'Appear offline' },
];

export function UserPanel() {
  const user = useAuth((s) => s.user);
  const presence = usePresenceOf(user?.id);
  const muted = useVoice((s) => s.muted);
  const deafened = useVoice((s) => s.deafened);
  const openModal = useUi((s) => s.openModal);
  const [menuOpen, setMenuOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  if (!user) return null;

  const setStatus = async (status: UserStatusPreference) => {
    try {
      const updated = await userApi.updateProfile({ status });
      useAuth.getState().setUser(updated);
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const clearStatus = async () => {
    try {
      useAuth.getState().setUser(await userApi.updateProfile({ customStatus: null }));
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const items: MenuEntry[] = [
    ...STATUS_OPTIONS.map((o) => ({
      label: o.label,
      hint: user.status === o.value ? '✓' : o.hint,
      icon: <span className={`block h-2.5 w-2.5 rounded-full ${o.dot}`} />,
      onSelect: () => void setStatus(o.value),
    })),
    'separator',
    {
      label: user.customStatus ? 'Edit custom status' : 'Set a custom status',
      hint: describeExpiry(user.customStatusExpiresAt) ?? undefined,
      icon: <Smile size={16} />,
      onSelect: () => openModal({ type: 'customStatus' }),
    },
    ...(user.customStatus ? [{ label: 'Clear custom status', icon: <X size={16} />, onSelect: () => void clearStatus() }] : []),
  ];

  return (
    <div data-cf="user-panel" className="safe-bottom shrink-0 p-3 pt-2">
      <div className="flex items-center gap-0.5 rounded-full bg-well p-1.5 pr-2">
        <button
          ref={anchor}
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Set status"
          aria-haspopup="menu"
          className="flex min-w-0 flex-1 items-center gap-2.5 rounded-full p-0.5 pr-2 text-left transition-colors hover:bg-hover"
        >
          <Avatar user={user} size={36} status={presence} ring="ring-well" />
          <span className="min-w-0 leading-tight">
            <StyledName user={user} className="block truncate text-[13px] font-bold text-fg">
              {user.displayName}
            </StyledName>
            <span className="block truncate text-[11px] text-fg-soft">
              {user.customStatusEmoji} {user.customStatus || `@${user.username}`}
            </span>
          </span>
        </button>
        <Menu open={menuOpen} onClose={() => setMenuOpen(false)} anchor={anchor} placement="top-start" items={items} label="Status" />
        <IconButton label={muted ? 'Unmute' : 'Mute'} onClick={() => voiceManager.setMuted(!muted)} className={muted ? 'text-danger' : undefined}>
          {muted ? <MicOff size={18} /> : <Mic size={18} />}
        </IconButton>
        <IconButton label={deafened ? 'Undeafen' : 'Deafen'} onClick={() => voiceManager.setDeafened(!deafened)} className={deafened ? 'text-danger' : undefined}>
          {deafened ? <HeadphoneOff size={18} /> : <Headphones size={18} />}
        </IconButton>
        <IconButton label="User settings" onClick={() => openModal({ type: 'userSettings' })}>
          <Settings size={18} />
        </IconButton>
      </div>
    </div>
  );
}

/** Shown above the user panel while connected to (or joining) a voice channel. */
export function VoicePanel() {
  const { channelId, serverId, status, video, screen } = useVoice(
    useShallow((s) => ({ channelId: s.channelId, serverId: s.serverId, status: s.status, video: s.video, screen: s.screen })),
  );
  const channel = useServers((s) => (channelId ? s.channels[channelId] : undefined));
  const server = useServers((s) => (serverId ? s.servers[serverId] : undefined));
  if (!channelId || status === 'idle') return null;

  return (
    <div className="mx-3 mt-2 shrink-0 rounded-3xl bg-accent-2-soft px-2 pt-2 pb-1.5">
      <div className="flex items-center gap-2 pl-1.5">
        <Signal size={18} className={status === 'connected' ? 'text-success' : 'animate-pulse text-warning'} />
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-bold ${status === 'connected' ? 'text-success' : 'text-warning'}`}>
            {status === 'connected' ? 'Voice connected' : 'Connecting…'}
          </p>
          <Link to={`/app/${serverId}/${channelId}`} className="block truncate text-xs text-accent-2-ink/80 hover:text-accent-2-ink hover:underline">
            {channel?.name} / {server?.name}
          </Link>
        </div>
        <IconButton label="Disconnect" tone="danger" onClick={() => voiceManager.leave()}>
          <PhoneOff size={18} />
        </IconButton>
      </div>
      {/* Camera, screen and soundboard get a row of their own, so the panel never gets cramped. */}
      <div className="mt-1.5 grid grid-flow-col gap-1">
        <IconButton label={video ? 'Turn off camera' : 'Turn on camera'} active={video} disabled={status !== 'connected'} onClick={() => void voiceManager.setVideo(!video)} className="h-8! w-full! rounded-xl!">
          {video ? <Video size={18} /> : <VideoOff size={18} />}
        </IconButton>
        {screenShareSupported() && (
          <IconButton
            label={screen ? 'Stop sharing' : 'Share your screen'}
            active={screen}
            disabled={status !== 'connected'}
            onClick={() => void voiceManager.setScreenShare(!screen)}
            className="h-8! w-full! rounded-xl!"
          >
            {screen ? <ScreenShareOff size={18} /> : <ScreenShare size={18} />}
          </IconButton>
        )}
        {serverId && status === 'connected' && <SoundboardButton serverId={serverId} className="h-8 w-full rounded-xl" />}
      </div>
    </div>
  );
}
